import {
  computeInitialSiteContextLayout,
  getEditor,
  groupPanelsInCanvas,
  openPanelInCanvas,
  autoArrangeSiteContextPanels,
  enterSiteWorkspaceMode,
  ejectGlobalPanelsFromSiteFrames,
  SITE_CONTEXT_VIEWPORT_INSET,
} from 'agentable-canvas/whiteboard';
import type { SiteContextPanelKind } from 'agentable-canvas/whiteboard';
import type { CanvasSessionRecord } from './canvas-session-types';
import {
  shouldOpenBriefOnSiteLoad,
  shouldOpenChatOnSiteLoad,
  shouldOpenFilesOnSiteLoad,
  shouldOpenPreviewOnSiteLoad,
} from './site-open-panel-policy';

const MAX_BIND_ATTEMPTS = 80;
const BIND_POLL_MS = 100;

/** Panels that may open when loading a site onto the canvas. */
export const SITE_PANEL_IDS = [
  'chat',
  'project-brief',
  'web-preview',
  'file-manager',
] as const;

export interface OpenSiteOnCanvasOptions {
  siteId: string;
  /** Optional agency id — nests the site frame under an agency workspace frame. */
  agencyId?: string | null;
  siteLabel?: string;
  focusPreview?: boolean;
  /** When set, drives default panel inclusion; omit to open brief + preview + files only. */
  session?: CanvasSessionRecord | null;
  includeChat?: boolean;
  includeBrief?: boolean;
  includePreview?: boolean;
  includeFiles?: boolean;
}

function resolvePanelFlags(options: OpenSiteOnCanvasOptions): {
  includeChat: boolean;
  includeBrief: boolean;
  includePreview: boolean;
  includeFiles: boolean;
} {
  const session = options.session ?? null;
  return {
    includeChat:
      options.includeChat ??
      (session ? shouldOpenChatOnSiteLoad(session) : false),
    includeBrief:
      options.includeBrief ??
      (session ? shouldOpenBriefOnSiteLoad(session) : false),
    includePreview:
      options.includePreview ??
      (session ? shouldOpenPreviewOnSiteLoad(session) : true),
    includeFiles:
      options.includeFiles ??
      (session ? shouldOpenFilesOnSiteLoad() : true),
  };
}

function panelPropsForKind(
  panelId: SiteContextPanelKind,
  siteId: string,
): Record<string, unknown> {
  switch (panelId) {
    case 'chat':
      return { __title: 'Chat', __siteId: siteId };
    case 'project-brief':
      return { __title: 'Project Brief', __siteId: siteId };
    case 'web-preview':
      return {
        __title: 'Draft preview',
        __hideChrome: true,
        __noBorder: true,
        __fullBleed: true,
        siteId,
        __siteId: siteId,
        refreshKey: Date.now(),
      };
    case 'file-manager':
      return {
        __title: 'File Manager',
        siteId,
        __siteId: siteId,
        refreshKey: Date.now(),
      };
    default: {
      const _exhaustive: never = panelId;
      return _exhaustive;
    }
  }
}

/**
 * Open site panels with 12-column grid layout; snap to grid; group under site frame.
 */
export function openSiteOnCanvas(options: OpenSiteOnCanvasOptions): () => void {
  let attempts = 0;
  let cancelled = false;

  const flags = resolvePanelFlags(options);

  const openPanels = (): boolean => {
    const editor = getEditor();
    if (!editor) return false;

    const viewport = editor.getViewportPageBounds();
    const layouts = computeInitialSiteContextLayout(
      {
        x: viewport.x + SITE_CONTEXT_VIEWPORT_INSET,
        y: viewport.y + SITE_CONTEXT_VIEWPORT_INSET,
        maxWidth: viewport.w - SITE_CONTEXT_VIEWPORT_INSET * 2,
        maxHeight: viewport.h - SITE_CONTEXT_VIEWPORT_INSET * 2,
      },
      {
        includeChat: flags.includeChat,
        includeBrief: flags.includeBrief,
        includePreview: flags.includePreview,
        includeFiles: flags.includeFiles,
        dockChatLeft: flags.includeChat,
      },
    );

    const sharedPanelOpts = {
      agencyId: options.agencyId,
      siteLabel: options.siteLabel,
      assignToSiteGroup: false,
    };

    const openedIds: string[] = [];

    for (const placement of layouts) {
      const panelId = placement.panelId;
      openedIds.push(panelId);
      const focusPreview = options.focusPreview ?? false;
      const focus =
        panelId === 'web-preview'
          ? focusPreview
          : panelId === 'chat' && !focusPreview;

      openPanelInCanvas(panelId, {
        ...sharedPanelOpts,
        focus,
        position: { x: placement.x, y: placement.y },
        size: { w: placement.w, h: placement.h },
        panelProps: panelPropsForKind(panelId, options.siteId),
      });
    }

    if (openedIds.length > 0) {
      ejectGlobalPanelsFromSiteFrames(editor);
      groupPanelsInCanvas(openedIds, {
        siteId: options.siteId,
        agencyId: options.agencyId,
        siteLabel: options.siteLabel,
      });
      autoArrangeSiteContextPanels(editor, options.siteId);
      enterSiteWorkspaceMode(editor, options.siteId, { arrangeFirst: false });
    }
    return true;
  };

  if (openPanels()) {
    return () => undefined;
  }

  const timer = window.setInterval(() => {
    if (cancelled) return;
    attempts += 1;
    if (openPanels() || attempts >= MAX_BIND_ATTEMPTS) {
      window.clearInterval(timer);
    }
  }, BIND_POLL_MS);

  return () => {
    cancelled = true;
    window.clearInterval(timer);
  };
}
